'use client';

import * as React from 'react';
import { Avatar } from '@/components/ds';
import { cn } from '@/lib/utils';
import {
  formatNotificationTime,
  getNotificationMeta,
  isUnread as isUnreadRead,
} from '@/lib/notifications';

// THE notification row. Both the bell dropdown (NotificationCenter) and the
// /notifications page render through this — they previously carried two
// divergent copies, which is how the dropdown ended up with no type icon, a
// standalone unread dot, and two timestamp formats while the page had none of
// those problems. Lifted out of the page implementation, which was the correct
// one.

export interface NotificationRowActor {
  name: string;
  username?: string | null;
  avatarUrl?: string | null;
}

export interface NotificationRowData {
  id: string;
  /** Raw DB `type`. The badge/icon resolve from it via getNotificationMeta. */
  type: string;
  title: string;
  /** Nullable in the DB, and empty on ~47% of rows (every `like`). */
  message?: string | null;
  actor?: NotificationRowActor | null;
  actionUrl?: string | null;
  createdAt: string | Date;
  /** Nullable boolean — see isUnread(). */
  read: boolean | null;
}

/**
 * Which themed surface the row sits on. The type badge draws a ring in this
 * token so it reads as separate from the avatar photo underneath, and the ring
 * only disappears correctly if it matches the actual backdrop.
 */
export type NotificationRowSurface = 'surface-1' | 'surface-2';

// Written out rather than interpolated so Tailwind's scanner sees both classes.
const BADGE_RING: Record<NotificationRowSurface, string> = {
  'surface-1': 'border-surface-1',
  'surface-2': 'border-surface-2',
};

export interface NotificationRowProps {
  notification: NotificationRowData;
  /** Fires only when the row is clickable. */
  onSelect?: (notification: NotificationRowData) => void;
  /** Whether the row responds to click/Enter/Space. */
  clickable?: boolean;
  /** Trailing controls (the page's mark-read + delete). Omitted in the dropdown. */
  actions?: React.ReactNode;
  surface?: NotificationRowSurface;
  className?: string;
}

/**
 * Trigger-written titles lead with the actor ("Kaylynx liked your post"), so
 * the name is emphasised in place instead of being repeated as a byline. Only a
 * genuine prefix match is treated as the actor — a name appearing mid-sentence
 * is left alone rather than being bolded somewhere arbitrary.
 */
function splitTitle(title: string, actorName?: string | null) {
  if (!actorName) return { lead: null, rest: title };
  const name = actorName.trim();
  if (!name || title.length < name.length) return { lead: null, rest: title };
  if (title.slice(0, name.length).toLowerCase() !== name.toLowerCase()) {
    return { lead: null, rest: title };
  }
  return { lead: title.slice(0, name.length), rest: title.slice(name.length) };
}

export function NotificationRow({
  notification,
  onSelect,
  clickable = false,
  actions,
  surface = 'surface-1',
  className,
}: NotificationRowProps) {
  const meta = getNotificationMeta(notification.type);
  const TypeIcon = meta.icon;
  const unread = isUnreadRead(notification.read);
  const actor = notification.actor || null;

  // Treat null and '' identically: no body element at all, not an empty node.
  // Reserving a blank line would waste a line on every `like` notification.
  const body = (notification.message || '').trim();
  const hasBody = body.length > 0;

  const { lead, rest } = splitTitle(notification.title, actor?.name);

  const handleClick = () => {
    if (clickable) onSelect?.(notification);
  };

  const content = (
    <>
      {/* Avatar with the type badge overlaid bottom-right. */}
      <div className="relative flex-shrink-0">
        {actor ? (
          <Avatar src={actor.avatarUrl || undefined} name={actor.name} size={36} />
        ) : (
          // No actor: render the type icon in a neutral well. Never fabricate
          // initials from a system string — a purple initials circle reads as a
          // real user who does not exist.
          <div
            className="w-9 h-9 rounded-full bg-surface-3 border border-border-subtle flex items-center justify-center"
            title={meta.label}
          >
            <TypeIcon className="w-4 h-4 text-text-secondary" strokeWidth={1.75} aria-hidden="true" />
            <span className="sr-only">{meta.label}</span>
          </div>
        )}
        {actor && (
          <span
            className={cn(
              'absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-accent text-text-on-accent',
              'flex items-center justify-center border-2',
              BADGE_RING[surface]
            )}
            title={meta.label}
          >
            <TypeIcon className="w-2 h-2" strokeWidth={2.5} aria-hidden="true" />
            {/* The page used to render the type as a visible Tag; at this
                density the badge carries it visually, so keep the words for
                screen readers rather than dropping the label outright. */}
            <span className="sr-only">{meta.label}</span>
          </span>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          {/* Single line. The title carries the meaning, so it is the one that
              must survive truncation intact. */}
          <span
            className={cn(
              'block min-w-0 flex-1 truncate text-[13px] leading-snug',
              unread ? 'text-text-primary' : 'text-text-secondary'
            )}
            title={notification.title}
          >
            {lead && <span className="font-semibold">{lead}</span>}
            {rest}
          </span>
          <span className="flex-shrink-0 text-[11px] leading-snug text-text-tertiary tabular-nums">
            {formatNotificationTime(notification.createdAt)}
          </span>
        </div>

        {hasBody && (
          <p
            className="mt-0.5 truncate text-[12px] leading-snug text-text-secondary"
            title={body}
          >
            {body}
          </p>
        )}
      </div>

      {actions && (
        <div className="flex flex-shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {actions}
        </div>
      )}
    </>
  );

  // Unread is carried by a 3px accent rule plus a faint tinted background. Read
  // rows keep the same 3px border in `transparent`, so the two states sit at
  // identical horizontal alignment and nothing shifts when a row is marked read.
  const rowClass = cn(
    'flex w-full gap-2.5 border-l-[3px] px-3.5 py-[11px] text-left transition-colors',
    hasBody ? 'items-start' : 'items-center',
    unread ? 'border-l-accent bg-accent-soft' : 'border-l-transparent',
    clickable && 'cursor-pointer hover:bg-surface-3',
    className
  );

  // Two heights by design: a row with a body is ~62px, a bodiless row ~50px.
  const rowStyle: React.CSSProperties = { minHeight: hasBody ? 62 : 50 };

  if (!clickable) {
    return (
      <div className={rowClass} style={rowStyle}>
        {content}
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={rowClass}
      style={rowStyle}
    >
      {content}
    </div>
  );
}

export default NotificationRow;
